import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Modal,
  Grid,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate, useLocation } from "react-router-dom";

function getCSRFToken() {
  const name = "csrftoken";
  const cookies = document.cookie.split(";");
  for (let c of cookies) {
    const t = c.trim();
    if (t.startsWith(name + "=")) return decodeURIComponent(t.substring(name.length + 1));
  }
  return null;
}

const ESTADO_OPTIONS = [
  { value: "activo", label: "Activo" },
  { value: "inactivo", label: "Inactivo" },
];

const AdminClub = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [productos, setProductos] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const [editImage, setEditImage] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    const s = location.state?.snackbar;
    if (s?.message) {
      setSnackbar({ open: true, type: s.type, message: s.message });
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const fetchProductos = async () => {
    try {
      const r = await fetch("/servicios/ver-productos-club/", { credentials: "include" });
      const d = await r.json();
      setProductos(d.productos || []);
    } catch {
      setProductos([]);
    }
  };

  useEffect(() => {
    fetchProductos();
  }, []);

  useEffect(() => {
    const t = search.toLowerCase();
    setFiltered(
      productos.filter(
        (p) =>
          p.code.toLowerCase().includes(t) ||
          p.name.toLowerCase().includes(t)
      )
    );
    setPage(0);
  }, [search, productos]);

  const handleDelete = async () => {
    try {
      const r = await fetch(`/servicios/eliminar-producto-club/${deleteId}/`, {
        method: "DELETE",
        headers: { "X-CSRFToken": getCSRFToken() },
        credentials: "include",
      });

      if (r.ok) {
        setSnackbar({ open: true, type: "success", message: "Producto eliminado correctamente" });
        fetchProductos();
      } else {
        setSnackbar({ open: true, type: "error", message: "No se pudo eliminar el producto" });
      }
    } catch {
      setSnackbar({ open: true, type: "error", message: "Error al eliminar producto" });
    } finally {
      setDeleteId(null);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    if (editImage) form.append("image", editImage);

    try {
      const r = await fetch(`/servicios/editar-producto-club/${selected.id}/`, {
        method: "POST",
        headers: { "X-CSRFToken": getCSRFToken() },
        credentials: "include",
        body: form,
      });

      if (r.ok) {
        setSnackbar({ open: true, type: "success", message: "Producto actualizado correctamente" });
        setEditOpen(false);
        setEditImage(null);
        setPreview(null);
        fetchProductos();
      } else {
        setSnackbar({ open: true, type: "error", message: "No se pudo actualizar el producto" });
      }
    } catch {
      setSnackbar({ open: true, type: "error", message: "Error al editar producto" });
    }
  };

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, mb: 3 }}
            onClick={() => navigate("/admin-panel/productoclub/nuevo")}
          >
            Nuevo Producto Club
          </Button>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <TextField
              fullWidth
              label="Buscar producto"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Código</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Producto</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Puntos</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }}>Estado</TableCell>
                    <TableCell sx={{ bgcolor: "#1565C0", color: "#fff" }} align="center">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.code}</TableCell>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>{p.puntos}</TableCell>
                        <TableCell>{p.estado === "activo" ? "Activo" : "Inactivo"}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{ bgcolor: "#1565C0" }}
                              onClick={() => {
                                setSelected(p);
                                setEditOpen(true);
                                setPreview(p.image);
                              }}
                            >
                              Editar
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="error"
                              onClick={() => setDeleteId(p.id)}
                            >
                              Eliminar
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, n) => setPage(n)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Paper>
        </Box>
      </Box>

      <Modal open={editOpen} onClose={() => setEditOpen(false)}>
        <Box
          component="form"
          onSubmit={handleEditSubmit}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 3,
            borderRadius: 2,
            width: 520,
          }}
        >
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField name="name" defaultValue={selected.name} fullWidth label="Nombre" />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="puntos"
                  defaultValue={selected.puntos}
                  fullWidth
                  label="Puntos"
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Estado</InputLabel>
                  <Select name="estado" defaultValue={selected.estado}>
                    {ESTADO_OPTIONS.map((e) => (
                      <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {preview && (
                  <Box textAlign="center">
                    <img src={preview} alt="preview" style={{ maxHeight: 150 }} />
                    <IconButton color="error" onClick={() => { setPreview(null); setEditImage(null); }}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
                <Button component="label" fullWidth>
                  Cambiar Imagen
                  <input hidden type="file" accept="image/*" onChange={(e) => {
                    setEditImage(e.target.files[0]);
                    setPreview(URL.createObjectURL(e.target.files[0]));
                  }} />
                </Button>
              </Grid>
              <Grid item xs={12} textAlign="right">
                <Button type="submit" variant="contained" sx={{ bgcolor: "#1565C0" }}>
                  Guardar Cambios
                </Button>
              </Grid>
            </Grid>
          )}
        </Box>
      </Modal>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 3,
            borderRadius: 2,
            width: 350,
          }}
        >
          <Typography variant="h6" mb={2}>¿Eliminar producto?</Typography>
          <Typography mb={3}>Esta acción no se puede deshacer.</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="contained"
              sx={{ bgcolor: "#1565C0" }}
              onClick={() => setDeleteId(null)}
            >
              Cancelar
            </Button>
            <Button variant="contained" color="error" onClick={handleDelete}>
              Eliminar
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.type} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminClub;
